/* Store Locator runtime
   - Loads data from <script type="application/json" id="store-locator-data">
   - Initializes MapLibre GL JS with a generated style
   - Geocodes locations via Nominatim (cached in localStorage)
   - Renders markers + fits bounds
   - Uses safe DOM construction (no innerHTML)
*/
(function () {
  'use strict';

  var SVG_NS = 'http://www.w3.org/2000/svg';
  var CACHE_PREFIX = 'sl-geo:';
  var GEOCODE_THROTTLE_MS = 1100; // Nominatim TOS: max 1 req/sec
  var POPUP_MAPS_TEXT = 'Voir sur Maps →'; // overridden in init() from section settings

  var LEAFLET_CSS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
  var LEAFLET_JS  = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';

  // WebGL feature detection — needed for the MapLibre vector renderer.
  // Returns false when hardware acceleration is off, the GPU is blocklisted,
  // running in a sandbox without WebGL, etc.
  function hasWebGL() {
    try {
      if (!window.WebGLRenderingContext) return false;
      var c = document.createElement('canvas');
      var gl = c.getContext('webgl') || c.getContext('experimental-webgl');
      return !!(gl && gl.getParameter);
    } catch (e) {
      return false;
    }
  }

  // Dynamically load Leaflet (CSS + JS) once, return a promise that
  // resolves when window.L is ready.
  var leafletLoading = null;
  function loadLeaflet() {
    if (window.L) return Promise.resolve(window.L);
    if (leafletLoading) return leafletLoading;
    leafletLoading = new Promise(function (resolve, reject) {
      if (!document.querySelector('link[data-sl-leaflet-css]')) {
        var link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = LEAFLET_CSS;
        link.setAttribute('data-sl-leaflet-css', '');
        document.head.appendChild(link);
      }
      var script = document.createElement('script');
      script.src = LEAFLET_JS;
      script.async = true;
      script.onload = function () { resolve(window.L); };
      script.onerror = function () { reject(new Error('Leaflet load failed')); };
      document.head.appendChild(script);
    });
    return leafletLoading;
  }

  function init() {
    var dataEl = document.getElementById('store-locator-data');
    if (!dataEl) return;

    var data;
    try {
      data = JSON.parse(dataEl.textContent);
    } catch (e) {
      console.error('[store-locator] Invalid JSON payload', e);
      return;
    }

    var mapEl = document.querySelector('[data-store-locator] .sl-map');
    if (!mapEl) return;
    mapEl.textContent = '';

    if (data.popup_maps_text) POPUP_MAPS_TEXT = data.popup_maps_text;

    // Decide which renderer to use: MapLibre (vector, requires WebGL) or
    // a raster Leaflet fallback (works everywhere — old browsers, sandboxed
    // Chrome, GPU blocklisted, etc.). MapLibre is preferred because it
    // honours the custom color palette; Leaflet uses a fixed raster style.
    if (typeof maplibregl === 'undefined' || !hasWebGL()) {
      console.info('[store-locator] WebGL unavailable — using Leaflet fallback');
      initLeafletFallback(data, mapEl);
      return;
    }

    // Wrap MapLibre init in try/catch in case WebGL detection passed but
    // the actual context creation still fails (some sandboxed iframes).
    var map;
    try {
      map = new maplibregl.Map({
        container: mapEl,
        style: buildMapStyle(data.map),
        center: data.map.default_center,
        zoom: data.map.default_zoom
      });
    } catch (e) {
      console.error('[store-locator] MapLibre init failed — falling back to Leaflet', e);
      initLeafletFallback(data, mapEl);
      return;
    }

    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');

    map.on('load', function () {
      var markerByLocationId = {};
      var resolvedGeocoded = [];

      // Render markers progressively as each coord is resolved so the
      // user sees results immediately (instead of waiting for all 21+
      // boutiques to finish geocoding).
      geocodeAll(data.locations, function onOne(entry) {
        if (!entry.coords) return;
        var single = renderMarkers(map, [entry], data);
        Object.keys(single).forEach(function (k) { markerByLocationId[k] = single[k]; });
        resolvedGeocoded.push(entry);
      }).then(function (geocoded) {
        if (data.map.fit_to_markers && geocoded.length > 0) {
          fitToMarkers(map, geocoded);
        }
        markBadgesForUngeocodable(data.locations, geocoded);
        bindInteractions(map, markerByLocationId);
        bindSearch(map, markerByLocationId, geocoded);
        bindFeaturedToggles();
        bindMobileTabs(map);
        bindProductTooltips();
      });
    });

    window.__storeLocator = { map: map, data: data };
  }

  // ─── Geocoding ──────────────────────────────────────────────────────────

  function getCached(address) {
    try {
      var raw = localStorage.getItem(CACHE_PREFIX + address);
      return raw ? JSON.parse(raw) : null;
    } catch (e) { return null; }
  }

  function setCached(address, coords) {
    try {
      localStorage.setItem(CACHE_PREFIX + address, JSON.stringify(coords));
    } catch (e) { /* quota or disabled — skip cache */ }
  }

  function sleep(ms) {
    return new Promise(function (resolve) { setTimeout(resolve, ms); });
  }

  async function geocodeOne(address, attempt) {
    attempt = attempt || 1;
    var url = 'https://nominatim.openstreetmap.org/search?format=json&limit=1&q=' + encodeURIComponent(address);
    try {
      var res = await fetch(url, { headers: { 'Accept-Language': 'fr' } });
      if (res.status === 429 && attempt < 3) {
        // Rate limited — backoff and retry
        await sleep(5000);
        return geocodeOne(address, attempt + 1);
      }
      if (!res.ok) throw new Error('HTTP ' + res.status);
      var data = await res.json();
      if (!data || !data.length) return null;
      return { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) };
    } catch (e) {
      console.warn('[store-locator] geocode failed for "' + address + '"', e);
      return null;
    }
  }

  // Parse a "lat, lng" string (the format Google Maps copies when you
  // click the coordinates on right-click menu). Accepts variable
  // whitespace. Returns null on invalid input.
  function parseCoordinatesString(s) {
    if (!s) return null;
    var parts = String(s).split(',');
    if (parts.length !== 2) return null;
    var lat = parseFloat(parts[0].trim());
    var lng = parseFloat(parts[1].trim());
    if (isNaN(lat) || isNaN(lng)) return null;
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
    return { lat: lat, lng: lng };
  }

  // Resolve a location's coords. Priority:
  //   1. Manually-set "coordinates" string on the block (instant)
  //   2. localStorage cache for the address (instant, second visit+)
  //   3. Nominatim geocoding (rate-limited to 1 req/sec per TOS)
  function resolveCoordsOne(loc) {
    var manual = parseCoordinatesString(loc.coordinates);
    if (manual) return Promise.resolve(manual);
    if (!loc.address) return Promise.resolve(null);
    var cached = getCached(loc.address);
    if (cached) return Promise.resolve(cached);
    return geocodeOne(loc.address).then(function (coords) {
      if (coords) setCached(loc.address, coords);
      return coords;
    });
  }

  // Resolve all coords. Manually-set + cached results resolve immediately,
  // only the truly-uncached addresses are throttled. Calls onProgress(r)
  // for each result so the caller can render markers incrementally.
  async function geocodeAll(locations, onProgress) {
    var results = [];
    for (var i = 0; i < locations.length; i++) {
      var loc = locations[i];
      var hasManualCoords = !!parseCoordinatesString(loc.coordinates);
      var hasCached = loc.address && getCached(loc.address);
      var coords = await resolveCoordsOne(loc);
      var entry = { location: loc, coords: coords };
      results.push(entry);
      if (typeof onProgress === 'function') onProgress(entry);
      // Only throttle when we actually hit Nominatim
      if (!hasManualCoords && !hasCached && loc.address) {
        await sleep(GEOCODE_THROTTLE_MS);
      }
    }
    return results.filter(function (r) { return r.coords !== null; });
  }

  // ─── Markers (safe DOM construction) ────────────────────────────────────

  function renderMarkers(map, geocoded, data) {
    var byId = {};
    // pin tip touches the coordinate; circle/image are centered on it
    var anchor = (data.marker.style === 'pin_default') ? 'bottom' : 'center';
    var popupOffset = (data.marker.style === 'pin_default') ? 30 : Math.round(data.marker.size / 2) + 8;

    geocoded.forEach(function (r) {
      var el = buildMarkerElement(data.marker);
      el.setAttribute('aria-label', r.location.title);
      el.setAttribute('data-marker-for', r.location.id);

      var popup = new maplibregl.Popup({
        offset: popupOffset,
        className: 'sl-popup',
        closeButton: true,
        closeOnClick: false, // marker click bubbles to the map — keep popup open
        closeOnMove: false,  // keep popup open during flyTo
        maxWidth: 'none'
      }).setDOMContent(buildPopupElement(r.location));

      // Sync sidebar + camera when popup opens — single source of truth
      // covering BOTH paths: user clicks marker (MapLibre auto-opens) OR
      // user clicks sidebar card (selectCard programmatically opens).
      popup.on('open', function () {
        markCardActive(r.location.id);
        var mobile = isMobileViewport();
        if (mobile) {
          // Scroll to the non-sticky anchor just above the tabs
          var anchor = document.querySelector('[data-store-locator] [data-tabs-anchor]');
          if (anchor) anchor.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        var padding = mobile
          ? { top: 40, bottom: 220, left: 20, right: 20 }
          : { top: 60, bottom: 60, left: 60, right: 60 };
        map.flyTo({ center: [r.coords.lng, r.coords.lat], zoom: 15, duration: 700, padding: padding });
        if (!mobile) {
          var card = document.querySelector('.sl-card[data-location-id="' + r.location.id + '"]');
          if (card) card.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      });

      var marker = new maplibregl.Marker({ element: el, anchor: anchor })
        .setLngLat([r.coords.lng, r.coords.lat])
        .setPopup(popup)
        .addTo(map);

      byId[r.location.id] = marker;
    });
    return byId;
  }

  // Module-scope helpers used by popup.on('open')
  function isMobileViewport() {
    return window.matchMedia('(max-width: 767px)').matches;
  }
  function markCardActive(locationId) {
    var cards = document.querySelectorAll('[data-store-locator] .sl-card');
    cards.forEach(function (c) {
      if (c.getAttribute('data-location-id') === locationId) {
        c.setAttribute('data-active', '');
      } else {
        c.removeAttribute('data-active');
      }
    });
  }

  function bindInteractions(map, markerByLocationId) {
    var cards = document.querySelectorAll('[data-store-locator] .sl-card');

    function selectCard(id) {
      // On mobile, switch to the map tab BEFORE opening the popup so the
      // map has the right dimensions when MapLibre positions the popup.
      var mobile = window.matchMedia('(max-width: 767px)').matches;
      if (mobile && typeof window.__storeLocatorSetView === 'function') {
        window.__storeLocatorSetView('map');
      }
      var marker = markerByLocationId[id];
      if (!marker) return;
      // Slight delay on mobile to let map.resize() settle before opening popup
      if (mobile) {
        setTimeout(function () {
          if (!marker.getPopup().isOpen()) marker.togglePopup();
        }, 120);
      } else {
        if (!marker.getPopup().isOpen()) marker.togglePopup();
      }
    }

    // Card → Map: click main area zooms + opens popup
    cards.forEach(function (card) {
      var id = card.getAttribute('data-location-id');
      var main = card.querySelector('[data-card-select]');
      if (!main) return;

      function isInteractiveChild(target) {
        // Native interactivity: links + the hours <details><summary>
        return !!(target.closest && target.closest('a, summary'));
      }

      main.addEventListener('click', function (e) {
        if (isInteractiveChild(e.target)) return;
        selectCard(id);
      });
      main.addEventListener('keydown', function (e) {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        if (isInteractiveChild(e.target)) return;
        e.preventDefault();
        selectCard(id);
      });

      card.addEventListener('mouseenter', function () {
        var el = document.querySelector('.sl-marker[data-marker-for="' + id + '"]');
        if (el) el.classList.add('sl-marker--hover');
      });
      card.addEventListener('mouseleave', function () {
        var el = document.querySelector('.sl-marker[data-marker-for="' + id + '"]');
        if (el) el.classList.remove('sl-marker--hover');
      });
    });


    // NOTE: marker click is handled by MapLibre natively (auto-toggles the
    // attached popup). We DON'T add our own click listener — doing so
    // creates a race condition that cancels MapLibre's popup-open. The
    // popup.on('open') callback above syncs the sidebar instead.
  }

  // ─── Product tooltip (portalled to document.body) ───────────────────────
  // The in-card tooltip is hidden by CSS. On hover, we clone it, append to
  // <body> with position: fixed, and remove it on unhover. This escapes any
  // ancestor with overflow:auto/hidden (the scrollable sidebar, or any host
  // theme wrapper with hidden overflow) that would otherwise clip it.

  function bindProductTooltips() {
    if (window.matchMedia('(hover: none)').matches) return; // skip touch devices
    var cells = document.querySelectorAll('[data-store-locator] .sl-product-cell');
    var current = null;

    function clearCurrent() {
      if (current) { current.remove(); current = null; }
    }

    function position(el, cellRect) {
      el.style.top = (cellRect.bottom + 6) + 'px';
      el.style.left = (cellRect.left + cellRect.width / 2) + 'px';
    }

    cells.forEach(function (cell) {
      var source = cell.querySelector('.sl-product-tooltip');
      if (!source) return;

      cell.addEventListener('mouseenter', function () {
        clearCurrent();
        var clone = source.cloneNode(true);
        clone.setAttribute('data-portal', '');
        document.body.appendChild(clone);
        position(clone, cell.getBoundingClientRect());
        current = clone;
      });

      cell.addEventListener('mouseleave', clearCurrent);
    });

    // If the user scrolls or resizes while a tooltip is open, hide it
    // (re-positioning on scroll is jittery; just hide).
    window.addEventListener('scroll', clearCurrent, { passive: true, capture: true });
    window.addEventListener('resize', clearCurrent);
  }

  // ─── Mobile tabs (Liste / Carte) ────────────────────────────────────────

  function bindMobileTabs(map) {
    var container = document.querySelector('[data-store-locator] .sl-container');
    var buttons = document.querySelectorAll('[data-store-locator] [data-mobile-tab-btn]');
    if (!container || buttons.length === 0) return;

    function setView(view) {
      container.setAttribute('data-mobile-view', view);
      buttons.forEach(function (b) {
        b.setAttribute('aria-selected', b.getAttribute('data-mobile-tab-btn') === view ? 'true' : 'false');
      });
      // MapLibre needs to recompute its dimensions when its container size changes
      if (view === 'map') {
        setTimeout(function () { map.resize(); }, 50);
      }
    }

    var tabsAnchor = document.querySelector('[data-store-locator] [data-tabs-anchor]');

    buttons.forEach(function (b) {
      b.addEventListener('click', function () {
        setView(b.getAttribute('data-mobile-tab-btn'));
        // Scroll the page so the (sticky) tabs land at viewport top.
        // We target a non-sticky anchor placed just before the tabs because
        // scrollIntoView is a no-op when called on a sticky-pinned element.
        if (tabsAnchor) tabsAnchor.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });

    // Expose so other handlers (card click) can switch the view on mobile
    window.__storeLocatorSetView = setView;
  }

  // ─── Featured products toggle ───────────────────────────────────────────

  function bindFeaturedToggles() {
    var toggles = document.querySelectorAll('[data-store-locator] [data-featured-toggle]');
    toggles.forEach(function (btn) {
      var targetId = btn.getAttribute('aria-controls');
      var panel = document.getElementById(targetId);
      if (!panel) return;
      btn.addEventListener('click', function (e) {
        e.stopPropagation();
        var open = panel.hidden;
        panel.hidden = !open;
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        btn.classList.toggle('sl-featured-toggle--open', open);
      });
    });
  }

  // ─── Search: text filter + proximity search ─────────────────────────────

  function bindSearch(map, markerByLocationId, geocoded) {
    var input = document.querySelector('[data-store-locator] [data-search-input]');
    if (!input) return;

    var cards = Array.prototype.slice.call(document.querySelectorAll('[data-store-locator] .sl-card-wrapper'));
    var list = document.querySelector('[data-store-locator] .sl-list');
    var emptyMsg = document.querySelector('[data-store-locator] [data-search-empty]');
    var counterNum = document.querySelector('[data-store-locator] [data-counter-num]');
    var counterPlural = document.querySelector('[data-store-locator] [data-counter-plural]');
    var hint = document.querySelector('[data-store-locator] [data-proximity-hint]');
    var geolocBtn = document.querySelector('[data-store-locator] [data-geoloc-btn]');
    var pill = document.querySelector('[data-store-locator] [data-search-pill]');
    var pillLabel = document.querySelector('[data-store-locator] [data-pill-label]');
    var pillRemove = document.querySelector('[data-store-locator] [data-pill-remove]');
    var originalOrder = cards.slice(); // for reset

    var searchContainer = document.querySelector('[data-store-locator] .sl-search');

    function showPill(label) {
      if (!pill) return;
      if (pillLabel) pillLabel.textContent = label;
      pill.hidden = false;
      input.classList.add('sl-search-input--hidden');
      input.value = '';
      if (searchContainer) searchContainer.classList.add('sl-search--has-pill');
    }
    function hidePill() {
      if (!pill) return;
      pill.hidden = true;
      input.classList.remove('sl-search-input--hidden');
      if (searchContainer) searchContainer.classList.remove('sl-search--has-pill');
    }
    function resetProximity() {
      hidePill();
      clearDistances();
      resetCardOrder();
      setHint(null);
      applyTextFilter('');
      input.value = '';
    }

    function normalize(s) {
      return String(s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
    }

    // Haversine — distance in km between two lat/lng pairs
    function distanceKm(lat1, lng1, lat2, lng2) {
      var R = 6371;
      var dLat = (lat2 - lat1) * Math.PI / 180;
      var dLng = (lng2 - lng1) * Math.PI / 180;
      var a = Math.sin(dLat/2) * Math.sin(dLat/2)
            + Math.cos(lat1*Math.PI/180) * Math.cos(lat2*Math.PI/180)
            * Math.sin(dLng/2) * Math.sin(dLng/2);
      return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    }

    function formatDistance(km) {
      if (km < 1) return Math.round(km * 1000) + ' m';
      if (km < 10) return km.toFixed(1).replace('.', ',') + ' km';
      return Math.round(km) + ' km';
    }

    function setHint(text) {
      if (!hint) return;
      if (text) { hint.textContent = text; hint.hidden = false; }
      else { hint.hidden = true; hint.textContent = ''; }
    }

    function clearDistances() {
      cards.forEach(function (wrapper) {
        var d = wrapper.querySelector('[data-distance]');
        if (d) { d.hidden = true; d.textContent = ''; }
      });
    }

    function resetCardOrder() {
      originalOrder.forEach(function (w) { list.appendChild(w); });
    }

    // Text filter (live, on input)
    function applyTextFilter(query) {
      var q = normalize(query.trim());
      var visible = 0;
      cards.forEach(function (wrapper) {
        var card = wrapper.querySelector('.sl-card');
        if (!card) return;
        var haystack = normalize(card.getAttribute('data-search') || '');
        var match = q === '' || haystack.indexOf(q) !== -1;
        wrapper.hidden = !match;
        if (match) visible++;
        var locId = card.getAttribute('data-location-id');
        var marker = markerByLocationId[locId];
        if (marker) marker.getElement().style.display = match ? '' : 'none';
      });
      if (counterNum) counterNum.textContent = String(visible);
      if (counterPlural) counterPlural.textContent = visible > 1 ? 's' : '';
      if (emptyMsg) emptyMsg.hidden = (visible > 0);
      if (q !== '' && visible > 0) {
        var visibleGeocoded = geocoded.filter(function (r) {
          var c = document.querySelector('.sl-card[data-location-id="' + r.location.id + '"]');
          return c && !c.closest('.sl-card-wrapper').hidden;
        });
        if (visibleGeocoded.length > 0) fitToMarkers(map, visibleGeocoded);
      }
    }

    // Proximity sort — applied after geocoding a query OR using geolocation
    function applyProximity(userLat, userLng, label) {
      // Show all cards, compute + display distance, sort by it
      var entries = geocoded.map(function (r) {
        return {
          location: r.location,
          coords: r.coords,
          distance: distanceKm(userLat, userLng, r.coords.lat, r.coords.lng)
        };
      });
      entries.sort(function (a, b) { return a.distance - b.distance; });

      // Ungeocodable locations have no coords — keep them at the end, no distance
      var ungeocodable = cards.filter(function (w) {
        var c = w.querySelector('.sl-card');
        return c && !geocoded.some(function (r) { return r.location.id === c.getAttribute('data-location-id'); });
      });

      // Reorder DOM + set distance labels
      entries.forEach(function (e) {
        var wrapper = document.querySelector('.sl-card[data-location-id="' + e.location.id + '"]').closest('.sl-card-wrapper');
        wrapper.hidden = false;
        list.appendChild(wrapper);
        var d = wrapper.querySelector('[data-distance]');
        if (d) { d.textContent = formatDistance(e.distance); d.hidden = false; }
        var m = markerByLocationId[e.location.id];
        if (m) m.getElement().style.display = '';
      });
      ungeocodable.forEach(function (w) {
        w.hidden = false;
        list.appendChild(w);
        var d = w.querySelector('[data-distance]');
        if (d) { d.hidden = true; d.textContent = ''; }
      });

      var total = entries.length + ungeocodable.length;
      if (counterNum) counterNum.textContent = String(total);
      if (counterPlural) counterPlural.textContent = total > 1 ? 's' : '';
      if (emptyMsg) emptyMsg.hidden = true;

      setHint('Triés par distance' + (label ? ' depuis ' + label : ''));

      // Recentre la map sur le point de référence + premier marker
      if (entries.length > 0) {
        var bounds = new maplibregl.LngLatBounds();
        bounds.extend([userLng, userLat]);
        bounds.extend([entries[0].coords.lng, entries[0].coords.lat]);
        map.fitBounds(bounds, { padding: 80, maxZoom: 14, duration: 800 });
      } else {
        map.flyTo({ center: [userLng, userLat], zoom: 12, duration: 800 });
      }
    }

    // Geocode the query (via existing geocodeOne which is cached + throttled)
    function searchByQuery(query) {
      setHint('Recherche en cours…');
      geocodeOne(query).then(function (coords) {
        if (!coords) {
          setHint('Adresse introuvable — affichage du filtre texte.');
          applyTextFilter(query);
          clearDistances();
          return;
        }
        applyProximity(coords.lat, coords.lng, query);
      });
    }

    // Geolocation (browser GPS)
    function searchByGeoloc() {
      if (!navigator.geolocation) {
        setHint('Géolocalisation non disponible sur ton navigateur.');
        return;
      }
      setHint('Récupération de ta position…');
      navigator.geolocation.getCurrentPosition(
        function (pos) {
          showPill('Votre position');
          applyProximity(pos.coords.latitude, pos.coords.longitude, 'votre position');
        },
        function (err) {
          if (err.code === err.PERMISSION_DENIED) setHint('Tu as refusé la géolocalisation.');
          else setHint('Impossible de récupérer ta position.');
        },
        { enableHighAccuracy: false, timeout: 10000, maximumAge: 5 * 60 * 1000 }
      );
    }

    // Bind input — typing = live text filter
    var t;
    input.addEventListener('input', function () {
      clearTimeout(t);
      if (input.value.trim() === '') {
        // Reset to original order + clear proximity state
        clearDistances();
        resetCardOrder();
        setHint(null);
        applyTextFilter('');
        return;
      }
      t = setTimeout(function () {
        // Live typing: text filter (does NOT clear distances if proximity was active —
        // user can refine after a proximity sort by typing to narrow visible matches)
        applyTextFilter(input.value);
      }, 120);
    });

    // Enter = proximity search via geocoding
    input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') {
        e.preventDefault();
        var q = input.value.trim();
        if (q === '') { clearDistances(); resetCardOrder(); setHint(null); applyTextFilter(''); return; }
        searchByQuery(q);
      } else if (e.key === 'Escape') {
        input.value = '';
        clearDistances();
        resetCardOrder();
        setHint(null);
        applyTextFilter('');
      }
    });

    // Geoloc button
    if (geolocBtn) geolocBtn.addEventListener('click', searchByGeoloc);

    // Pill × button — remove pill + reset proximity state
    if (pillRemove) pillRemove.addEventListener('click', resetProximity);
  }

  function buildMarkerElement(markerCfg) {
    // Outer element — MapLibre rewrites its transform every frame for geo positioning.
    // We must NEVER set CSS transition on it, otherwise pan/zoom lags.
    var el = document.createElement('div');
    el.className = 'sl-marker';
    el.style.width = markerCfg.size + 'px';
    el.style.height = markerCfg.size + 'px';

    // Inner element — owns hover scale animation. Isolated from MapLibre's transform.
    var inner = document.createElement('div');
    inner.className = 'sl-marker-inner';
    inner.style.width = '100%';
    inner.style.height = '100%';
    inner.style.cursor = 'pointer';
    el.appendChild(inner);

    if (markerCfg.style === 'custom_image' && markerCfg.image_url) {
      if (/^https?:\/\//.test(markerCfg.image_url)) {
        var safeUrl = markerCfg.image_url.replace(/["'\\]/g, encodeURIComponent);
        inner.style.backgroundImage = "url('" + safeUrl + "')";
        inner.style.backgroundSize = 'contain';
        inner.style.backgroundRepeat = 'no-repeat';
        inner.style.backgroundPosition = 'center bottom';
        return el;
      }
    }

    if (markerCfg.style === 'circle') {
      var circleSvg = document.createElementNS(SVG_NS, 'svg');
      circleSvg.setAttribute('viewBox', '0 0 24 24');
      circleSvg.setAttribute('width', '100%');
      circleSvg.setAttribute('height', '100%');
      var outer = document.createElementNS(SVG_NS, 'circle');
      outer.setAttribute('cx', '12'); outer.setAttribute('cy', '12'); outer.setAttribute('r', '10');
      outer.setAttribute('fill', markerCfg.color);
      outer.setAttribute('stroke', '#ffffff'); outer.setAttribute('stroke-width', '2');
      circleSvg.appendChild(outer);
      inner.appendChild(circleSvg);
      return el;
    }

    // Default SVG pin
    var svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('viewBox', '0 0 24 32');
    svg.setAttribute('width', '100%');
    svg.setAttribute('height', '100%');

    var path = document.createElementNS(SVG_NS, 'path');
    path.setAttribute('d', 'M12 0C5.4 0 0 5.4 0 12c0 9 12 20 12 20s12-11 12-20C24 5.4 18.6 0 12 0z');
    path.setAttribute('fill', markerCfg.color);
    svg.appendChild(path);

    var dot = document.createElementNS(SVG_NS, 'circle');
    dot.setAttribute('cx', '12'); dot.setAttribute('cy', '12'); dot.setAttribute('r', '4');
    dot.setAttribute('fill', '#ffffff');
    svg.appendChild(dot);

    inner.appendChild(svg);
    return el;
  }

  function buildPopupElement(loc) {
    var root = document.createElement('div');
    root.setAttribute('data-store-locator-popup', '');

    var title = document.createElement('span');
    title.className = 'sl-popup-title';
    title.textContent = loc.title || '';
    root.appendChild(title);

    if (loc.subtitle) {
      var sub = document.createElement('span');
      sub.className = 'sl-popup-subtitle';
      sub.textContent = loc.subtitle;
      root.appendChild(sub);
    }

    if (loc.address) {
      var addr = document.createElement('p');
      addr.className = 'sl-popup-address';
      addr.textContent = loc.address;
      root.appendChild(addr);
    }

    if (loc.phone) {
      var phoneWrap = document.createElement('span');
      phoneWrap.className = 'sl-popup-phone';
      var phoneLink = document.createElement('a');
      phoneLink.href = 'tel:' + loc.phone.replace(/\s/g, '');
      phoneLink.textContent = loc.phone;
      phoneWrap.appendChild(phoneLink);
      root.appendChild(phoneWrap);
    }

    if (loc.maps_url || loc.address) {
      var mapsLink = document.createElement('a');
      mapsLink.className = 'sl-popup-maps';
      mapsLink.href = loc.maps_url || ('https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent(loc.address));
      mapsLink.target = '_blank';
      mapsLink.rel = 'noopener';
      mapsLink.textContent = POPUP_MAPS_TEXT;
      root.appendChild(mapsLink);
    }

    return root;
  }

  function fitToMarkers(map, geocoded) {
    if (geocoded.length === 1) {
      map.flyTo({ center: [geocoded[0].coords.lng, geocoded[0].coords.lat], zoom: 14 });
      return;
    }
    var bounds = new maplibregl.LngLatBounds();
    geocoded.forEach(function (r) {
      bounds.extend([r.coords.lng, r.coords.lat]);
    });
    map.fitBounds(bounds, { padding: 60, maxZoom: 15, duration: 800 });
  }

  function markBadgesForUngeocodable(allLocations, geocoded) {
    var geocodedIds = {};
    geocoded.forEach(function (r) { geocodedIds[r.location.id] = true; });
    allLocations.forEach(function (loc) {
      if (!geocodedIds[loc.id]) {
        var card = document.querySelector('.sl-card[data-location-id="' + loc.id + '"]');
        if (card && !card.querySelector('.sl-card-badge')) {
          var badge = document.createElement('span');
          badge.className = 'sl-card-badge';
          badge.textContent = 'Adresse non localisée';
          card.appendChild(badge);
        }
      }
    });
  }

  // ─── Map style ──────────────────────────────────────────────────────────

  function getColors(mapConfig) {
    var presets = {
      npc_rose:     { water: '#fce4e6', land: '#fff6ec', roads_major: '#ffffff', roads_minor: '#fde8d3',
                      buildings: '#f1dccd', parks: '#dde9c8', labels: '#5a2a35', borders: '#e8c8c8' },
      npc_bordeaux: { water: '#e8d4c0', land: '#fdf2e3', roads_major: '#ffffff', roads_minor: '#f0dfcc',
                      buildings: '#d8c2a8', parks: '#c8d5a8', labels: '#4a1a25', borders: '#c8a890' },
      npc_corail:   { water: '#fff1ec', land: '#ffffff', roads_major: '#fafafa', roads_minor: '#fde8de',
                      buildings: '#f4e3d3', parks: '#e0e8c8', labels: '#c93838', borders: '#f0c8b8' },
      npc_bleu:     { water: '#c8dce8', land: '#fff6ec', roads_major: '#ffffff', roads_minor: '#e6d8c8',
                      buildings: '#e0cab8', parks: '#d8e0c8', labels: '#5a2a35', borders: '#bccdd8' },
      npc_vert:     { water: '#d8e8e0', land: '#fff6ec', roads_major: '#ffffff', roads_minor: '#ebe0c8',
                      buildings: '#e8d8c8', parks: '#b8d0a8', labels: '#5a2a35', borders: '#c8d8c0' }
    };
    if (mapConfig.preset && mapConfig.preset !== 'custom' && presets[mapConfig.preset]) {
      return presets[mapConfig.preset];
    }
    return mapConfig.colors || presets.npc_rose;
  }

  function buildMapStyle(mapConfig) {
    var c = getColors(mapConfig);

    return {
      version: 8,
      glyphs: 'https://tiles.openfreemap.org/fonts/{fontstack}/{range}.pbf',
      sources: { openfreemap: { type: 'vector', url: 'https://tiles.openfreemap.org/planet' } },
      layers: [
        { id: 'bg', type: 'background', paint: { 'background-color': c.land } },
        { id: 'water', type: 'fill', source: 'openfreemap', 'source-layer': 'water', paint: { 'fill-color': c.water } },
        { id: 'parks', type: 'fill', source: 'openfreemap', 'source-layer': 'landuse',
          filter: ['in', ['get', 'class'], ['literal', ['park', 'cemetery', 'grass', 'wood']]],
          paint: { 'fill-color': c.parks, 'fill-opacity': 0.5 } },
        { id: 'buildings', type: 'fill', source: 'openfreemap', 'source-layer': 'building',
          minzoom: 13, paint: { 'fill-color': c.buildings } },
        { id: 'roads-minor', type: 'line', source: 'openfreemap', 'source-layer': 'transportation',
          filter: ['in', ['get', 'class'], ['literal', ['minor', 'service', 'tertiary']]],
          paint: { 'line-color': c.roads_minor, 'line-width': ['interpolate', ['linear'], ['zoom'], 10, 0.5, 16, 2] } },
        { id: 'roads-major', type: 'line', source: 'openfreemap', 'source-layer': 'transportation',
          filter: ['in', ['get', 'class'], ['literal', ['motorway', 'trunk', 'primary', 'secondary']]],
          paint: { 'line-color': c.roads_major, 'line-width': ['interpolate', ['linear'], ['zoom'], 6, 0.5, 16, 4] } },
        { id: 'borders', type: 'line', source: 'openfreemap', 'source-layer': 'boundary',
          filter: ['<=', ['get', 'admin_level'], 4],
          paint: { 'line-color': c.borders, 'line-width': 0.5, 'line-dasharray': [2, 2] } },
        { id: 'labels-place', type: 'symbol', source: 'openfreemap', 'source-layer': 'place',
          filter: ['in', ['get', 'class'], ['literal', ['city', 'town', 'village']]],
          layout: {
            // Prefer the visitor's language label (name:fr / name:en) with
            // graceful fallback to the local-language `name` field.
            'text-field': ['coalesce', ['get', getNameField()], ['get', 'name']],
            'text-font': ['Noto Sans Regular'],
            'text-size': 12
          },
          paint: { 'text-color': c.labels, 'text-halo-color': '#ffffff', 'text-halo-width': 1 } }
      ]
    };
  }

  // Return the OpenFreeMap name field matching the visitor's language
  // (e.g. "name:fr" for French Chrome). Fallback handled at use site via
  // coalesce with the local-language `name`.
  function getNameField() {
    var lang = (navigator.language || navigator.userLanguage || 'en').toLowerCase().split('-')[0];
    return 'name:' + lang;
  }

  // Tile provider for the Leaflet fallback. Merchant-selectable.
  function getLeafletTileLayer(provider) {
    var providers = {
      // OSM standard "Mapnik" — French region/city names worldwide for
      // French content. More colourful but the CSS filter softens it.
      osm: {
        url: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
        options: { maxZoom: 19, attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' }
      },
      // CARTO Positron — very light, minimal. Region names appear in
      // English (Brittany, Normandy) but city names stay local.
      carto_light: {
        url: 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png',
        options: { subdomains: 'abcd', maxZoom: 19, attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> © <a href="https://carto.com/attributions">CARTO</a>' }
      },
      // Same as above but no labels at all — clean backdrop where only
      // our markers stand out.
      carto_light_nolabels: {
        url: 'https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png',
        options: { subdomains: 'abcd', maxZoom: 19, attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> © <a href="https://carto.com/attributions">CARTO</a>' }
      }
    };
    return providers[provider] || providers.osm;
  }

  // ─── Leaflet fallback (raster tiles, no WebGL required) ─────────────────

  function initLeafletFallback(data, mapEl) {
    mapEl.textContent = '';
    var loading = document.createElement('p');
    loading.className = 'sl-map-fallback';
    loading.textContent = 'Chargement de la carte…';
    mapEl.appendChild(loading);

    loadLeaflet().then(function (L) {
      mapEl.textContent = '';

      var defLat = data.map.default_center[1];
      var defLng = data.map.default_center[0];
      var map = L.map(mapEl, { zoomControl: true, attributionControl: true })
        .setView([defLat, defLng], data.map.default_zoom);

      var tiles = getLeafletTileLayer(data.tile_provider);
      L.tileLayer(tiles.url, tiles.options).addTo(map);

      var markerByLocationId = {};
      geocodeAll(data.locations, function onOne(entry) {
        if (!entry.coords) return;
        var single = renderLeafletMarkers(L, map, [entry], data);
        Object.keys(single).forEach(function (k) { markerByLocationId[k] = single[k]; });
      }).then(function (geocoded) {
        if (data.map.fit_to_markers && geocoded.length > 0) {
          var bounds = L.latLngBounds(geocoded.map(function (r) { return [r.coords.lat, r.coords.lng]; }));
          map.fitBounds(bounds, { padding: [40, 40], maxZoom: 15 });
        }
        markBadgesForUngeocodable(data.locations, geocoded);
        bindLeafletInteractions(L, map, markerByLocationId, geocoded);
        bindLeafletSearch(L, map, markerByLocationId, geocoded);
        bindFeaturedToggles();
        bindLeafletMobileTabs(map);
        bindProductTooltips();
      });

      window.__storeLocator = { map: map, data: data, lib: 'leaflet' };
    }).catch(function (e) {
      console.error('[store-locator] Leaflet load failed', e);
      showMapFallback(mapEl);
      bindFallbackUI();
    });
  }

  function renderLeafletMarkers(L, map, geocoded, data) {
    var byId = {};
    var size = data.marker.size || 32;
    var color = data.marker.color || '#e62440';
    geocoded.forEach(function (r) {
      var html = '<span class="sl-leaflet-pin" style="--c:' + color + '">' +
        '<svg viewBox="0 0 24 32" width="' + size + '" height="' + Math.round(size * 1.3) + '" xmlns="http://www.w3.org/2000/svg">' +
          '<path d="M12 0C5.4 0 0 5.4 0 12c0 9 12 20 12 20s12-11 12-20C24 5.4 18.6 0 12 0z" fill="' + color + '"/>' +
          '<circle cx="12" cy="12" r="4" fill="#ffffff"/>' +
        '</svg></span>';
      var icon = L.divIcon({
        className: 'sl-leaflet-marker',
        html: html,
        iconSize: [size, Math.round(size * 1.3)],
        iconAnchor: [size / 2, Math.round(size * 1.3)],
        popupAnchor: [0, -Math.round(size * 1.3)]
      });
      var marker = L.marker([r.coords.lat, r.coords.lng], { icon: icon }).addTo(map);
      marker.bindPopup(buildPopupElement(r.location), {
        className: 'sl-popup',
        closeButton: true,
        autoClose: false,
        closeOnClick: false,
        maxWidth: 340
      });
      marker.on('popupopen', function () {
        markCardActive(r.location.id);
        if (!isMobileViewport()) {
          var card = document.querySelector('.sl-card[data-location-id="' + r.location.id + '"]');
          if (card) card.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      });
      // Tag the marker DOM element so the hover/search visibility logic works
      marker.getElement && marker.getElement(); // no-op (icon DOM created lazily)
      byId[r.location.id] = marker;
    });
    return byId;
  }

  function bindLeafletInteractions(L, map, markerByLocationId, geocoded) {
    var cards = document.querySelectorAll('[data-store-locator] .sl-card');
    var coordsByLocationId = {};
    geocoded.forEach(function (r) { coordsByLocationId[r.location.id] = r.coords; });

    cards.forEach(function (card) {
      var id = card.getAttribute('data-location-id');
      var main = card.querySelector('[data-card-select]');
      if (!main) return;
      function isInteractiveChild(target) {
        return !!(target.closest && target.closest('a, summary, button'));
      }
      function selectLeaflet(e) {
        if (e && isInteractiveChild(e.target)) return;
        var coords = coordsByLocationId[id];
        var marker = markerByLocationId[id];
        if (!coords || !marker) return;
        var mobile = isMobileViewport();
        if (mobile && typeof window.__storeLocatorSetView === 'function') {
          window.__storeLocatorSetView('map');
          setTimeout(function () { map.invalidateSize(); map.flyTo([coords.lat, coords.lng], 15, { duration: 0.7 }); marker.openPopup(); }, 150);
        } else {
          map.flyTo([coords.lat, coords.lng], 15, { duration: 0.7 });
          marker.openPopup();
        }
      }
      main.addEventListener('click', selectLeaflet);
      main.addEventListener('keydown', function (e) {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        if (isInteractiveChild(e.target)) return;
        e.preventDefault();
        selectLeaflet(e);
      });
    });
  }

  function bindLeafletSearch(L, map, markerByLocationId, geocoded) {
    var input = document.querySelector('[data-store-locator] [data-search-input]');
    if (!input) return;

    var cards = Array.prototype.slice.call(document.querySelectorAll('[data-store-locator] .sl-card-wrapper'));
    var list = document.querySelector('[data-store-locator] .sl-list');
    var emptyMsg = document.querySelector('[data-store-locator] [data-search-empty]');
    var counterNum = document.querySelector('[data-store-locator] [data-counter-num]');
    var counterPlural = document.querySelector('[data-store-locator] [data-counter-plural]');
    var hint = document.querySelector('[data-store-locator] [data-proximity-hint]');
    var geolocBtn = document.querySelector('[data-store-locator] [data-geoloc-btn]');
    var pill = document.querySelector('[data-store-locator] [data-search-pill]');
    var pillLabel = document.querySelector('[data-store-locator] [data-pill-label]');
    var pillRemove = document.querySelector('[data-store-locator] [data-pill-remove]');
    var searchContainer = document.querySelector('[data-store-locator] .sl-search');
    var originalOrder = cards.slice();

    function normalize(s) { return String(s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, ''); }
    function distanceKm(lat1, lng1, lat2, lng2) {
      var R = 6371, dLat = (lat2 - lat1) * Math.PI / 180, dLng = (lng2 - lng1) * Math.PI / 180;
      var a = Math.sin(dLat/2)*Math.sin(dLat/2) + Math.cos(lat1*Math.PI/180)*Math.cos(lat2*Math.PI/180)*Math.sin(dLng/2)*Math.sin(dLng/2);
      return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    }
    function formatDistance(km) {
      if (km < 1) return Math.round(km * 1000) + ' m';
      if (km < 10) return km.toFixed(1).replace('.', ',') + ' km';
      return Math.round(km) + ' km';
    }
    function setHint(text) {
      if (!hint) return;
      if (text) { hint.textContent = text; hint.hidden = false; }
      else { hint.hidden = true; hint.textContent = ''; }
    }
    function clearDistances() {
      cards.forEach(function (w) { var d = w.querySelector('[data-distance]'); if (d) { d.hidden = true; d.textContent = ''; } });
    }
    function resetCardOrder() { originalOrder.forEach(function (w) { list.appendChild(w); }); }
    function showPill(label) {
      if (!pill) return;
      if (pillLabel) pillLabel.textContent = label;
      pill.hidden = false;
      input.classList.add('sl-search-input--hidden');
      input.value = '';
      if (searchContainer) searchContainer.classList.add('sl-search--has-pill');
    }
    function hidePill() {
      if (!pill) return;
      pill.hidden = true;
      input.classList.remove('sl-search-input--hidden');
      if (searchContainer) searchContainer.classList.remove('sl-search--has-pill');
    }
    function resetProximity() {
      hidePill(); clearDistances(); resetCardOrder(); setHint(null); applyTextFilter(''); input.value = '';
    }

    function applyTextFilter(query) {
      var q = normalize(query.trim()), visible = 0;
      cards.forEach(function (w) {
        var card = w.querySelector('.sl-card'); if (!card) return;
        var match = q === '' || normalize(card.getAttribute('data-search') || '').indexOf(q) !== -1;
        w.hidden = !match;
        if (match) visible++;
        var marker = markerByLocationId[card.getAttribute('data-location-id')];
        if (marker) { if (match) marker.addTo(map); else map.removeLayer(marker); }
      });
      if (counterNum) counterNum.textContent = String(visible);
      if (counterPlural) counterPlural.textContent = visible > 1 ? 's' : '';
      if (emptyMsg) emptyMsg.hidden = (visible > 0);
    }

    function applyProximity(userLat, userLng, label) {
      var entries = geocoded.map(function (r) {
        return { location: r.location, coords: r.coords, distance: distanceKm(userLat, userLng, r.coords.lat, r.coords.lng) };
      });
      entries.sort(function (a, b) { return a.distance - b.distance; });
      entries.forEach(function (e) {
        var w = document.querySelector('.sl-card[data-location-id="' + e.location.id + '"]').closest('.sl-card-wrapper');
        w.hidden = false; list.appendChild(w);
        var d = w.querySelector('[data-distance]'); if (d) { d.textContent = formatDistance(e.distance); d.hidden = false; }
        var m = markerByLocationId[e.location.id]; if (m) m.addTo(map);
      });
      if (counterNum) counterNum.textContent = String(entries.length);
      if (counterPlural) counterPlural.textContent = entries.length > 1 ? 's' : '';
      if (emptyMsg) emptyMsg.hidden = true;
      setHint('Triés par distance' + (label ? ' depuis ' + label : ''));
      if (entries.length > 0) {
        map.flyToBounds(L.latLngBounds([[userLat, userLng], [entries[0].coords.lat, entries[0].coords.lng]]), { padding: [80, 80], maxZoom: 14, duration: 0.8 });
      } else {
        map.flyTo([userLat, userLng], 12, { duration: 0.8 });
      }
    }

    function searchByQuery(query) {
      setHint('Recherche en cours…');
      geocodeOne(query).then(function (coords) {
        if (!coords) { setHint('Adresse introuvable — affichage du filtre texte.'); applyTextFilter(query); clearDistances(); return; }
        applyProximity(coords.lat, coords.lng, query);
      });
    }
    function searchByGeoloc() {
      if (!navigator.geolocation) { setHint('Géolocalisation non disponible sur ton navigateur.'); return; }
      setHint('Récupération de ta position…');
      navigator.geolocation.getCurrentPosition(
        function (pos) { showPill('Votre position'); applyProximity(pos.coords.latitude, pos.coords.longitude, 'votre position'); },
        function (err) { setHint(err.code === err.PERMISSION_DENIED ? 'Tu as refusé la géolocalisation.' : 'Impossible de récupérer ta position.'); },
        { enableHighAccuracy: false, timeout: 10000, maximumAge: 5 * 60 * 1000 }
      );
    }

    var t;
    input.addEventListener('input', function () {
      clearTimeout(t);
      if (input.value.trim() === '') { clearDistances(); resetCardOrder(); setHint(null); applyTextFilter(''); return; }
      t = setTimeout(function () { applyTextFilter(input.value); }, 120);
    });
    input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') {
        e.preventDefault();
        var q = input.value.trim();
        if (q === '') { resetProximity(); return; }
        searchByQuery(q);
      } else if (e.key === 'Escape') { resetProximity(); }
    });
    if (geolocBtn) geolocBtn.addEventListener('click', searchByGeoloc);
    if (pillRemove) pillRemove.addEventListener('click', resetProximity);
  }

  function bindLeafletMobileTabs(map) {
    var container = document.querySelector('[data-store-locator] .sl-container');
    var buttons = document.querySelectorAll('[data-store-locator] [data-mobile-tab-btn]');
    if (!container || buttons.length === 0) return;
    var tabsAnchor = document.querySelector('[data-store-locator] [data-tabs-anchor]');

    function setView(view) {
      container.setAttribute('data-mobile-view', view);
      buttons.forEach(function (b) {
        b.setAttribute('aria-selected', b.getAttribute('data-mobile-tab-btn') === view ? 'true' : 'false');
      });
      if (view === 'map' && map && map.invalidateSize) {
        setTimeout(function () { map.invalidateSize(); }, 50);
      }
    }
    buttons.forEach(function (b) {
      b.addEventListener('click', function () {
        setView(b.getAttribute('data-mobile-tab-btn'));
        if (tabsAnchor) tabsAnchor.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
    window.__storeLocatorSetView = setView;
  }

  function showMapFallback(mapEl, message) {
    mapEl.textContent = '';
    var p = document.createElement('p');
    p.className = 'sl-map-fallback';
    p.textContent = message || 'Carte indisponible.';
    mapEl.appendChild(p);
  }

  // Bind UI that does NOT depend on the map (toggles, tooltips). Called
  // when the map fails to initialise so the rest of the page stays usable.
  function bindFallbackUI() {
    bindFeaturedToggles();
    bindProductTooltips();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
